<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import UhSpinner from '~/components/general/uhSpinner/UhSpinner.vue';
import type { User } from '~/types/user';
import LivePropertyModal from '~/components/navigation/appSidebar/LivePropertiesModal.vue';
import LivePropertiesIndicatorBar from '~/components/navigation/appSidebar/LivePropertiesIndicatorBar.vue';
import PortfolioConversionIndicator from '~/components/navigation/appSidebar/PortfolioConversionIndicator.vue';
import { useMainStore } from '~/stores/mainStore';
import {
  formatSpareRoomLimit,
  hasSpareRoomLimitAccess,
} from '~/utils/spareRoomLimit';

const openModal = ref<boolean>(false);

function toggleModal(): void {
  openModal.value = !openModal.value;
}

const { user }: { user: User } = useAuth();
const { livePropertiesCount, spareRoomPropertiesCount, propertiesCount, siteSettings } =
  storeToRefs(useMainStore());

const hasSpareRooms = computed(() =>
  hasSpareRoomLimitAccess(
    spareRoomPropertiesCount.value.limit,
    spareRoomPropertiesCount.value.live,
  ),
);
</script>

<template>
  <div>
    <div
      class="mb-0 w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-500"
    >
      <button
        type="button"
        class="group w-full text-left hover:bg-white"
        @click="toggleModal"
      >
        <span class="mb-4 block font-bold text-gray-900 dark:text-white">
          {{ user?.landlord?.company }}
        </span>

        <template v-if="!livePropertiesCount.pending">
          <div class="mb-2 flex justify-between">
            <span class="mr-4 text-sm font-medium text-gray-500 dark:text-white">
              Live properties
            </span>
            <span class="text-sm font-medium text-gray-500 dark:text-white">
              {{ livePropertiesCount.live.toLocaleString() }}/{{
                (livePropertiesCount.limit ?? 0).toLocaleString()
              }}
            </span>
          </div>

          <LivePropertiesIndicatorBar />

          <template v-if="hasSpareRooms">
            <div class="mb-2 mt-3 flex justify-between">
              <span
                class="mr-4 text-sm font-medium text-gray-500 dark:text-white"
              >
                Spare rooms
              </span>
              <span class="text-sm font-medium text-gray-500 dark:text-white">
                {{ spareRoomPropertiesCount.live.toLocaleString() }}/{{
                  formatSpareRoomLimit(spareRoomPropertiesCount.limit)
                }}
              </span>
            </div>

            <LivePropertiesIndicatorBar
              :count="spareRoomPropertiesCount"
              :change-colour="true"
            />
          </template>
        </template>

        <template v-else>
          <UhSpinner size="8" />
        </template>
      </button>
    </div>

    <PortfolioConversionIndicator
      v-if="siteSettings.showAgentPerformanceIndicator"
    />

    <LivePropertyModal
      :open="openModal"
      :properties-limit="livePropertiesCount.limit ?? 0"
      :properties-live="livePropertiesCount.live"
      :properties-count="propertiesCount"
      @toggle-modal="toggleModal"
    />
  </div>
</template>
